// ===========================================
// Class Links Section — the "Links" block inside Class Resources (2026-09-07)
// ===========================================
// Shared by the admin class page and the teacher class panel so both show the
// same list, the same Add Link dialog and the same delete confirm. Needs no
// Google Drive connection (Andrew D7). Three states, never collapsed: loading,
// error, genuinely empty.

import { useState } from 'react';
import { Alert, Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import { Link as LinkIcon, AddLink as AddLinkIcon } from '@mui/icons-material';
import { ClassLink } from '../../api/classLinks.api';
import { useClassLinks, useDeleteClassLink } from '../../hooks/useClassLinks';
import LinkCard from './LinkCard';
import ClassLinkDialog from './ClassLinkDialog';
import ConfirmDialog from '../common/ConfirmDialog';

export interface ClassLinksSectionProps {
  lessonId: string;
  programLevel?: { id: string; name: string } | null;
  editable?: boolean;
}

export default function ClassLinksSection({ lessonId, programLevel, editable = false }: ClassLinksSectionProps) {
  const { data: links, isLoading, isError } = useClassLinks({ lessonId });
  const deleteLink = useDeleteClassLink();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ClassLink | null>(null);
  const [toDelete, setToDelete] = useState<ClassLink | null>(null);

  const isEmpty = !isLoading && !isError && (links?.length ?? 0) === 0;

  return (
    <Box data-testid="class-links-section" sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon fontSize="small" /> Links
        </Typography>
        {editable && (
          <Button variant="outlined" size="small" startIcon={<AddLinkIcon />} onClick={() => { setEditing(null); setDialogOpen(true); }}>
            Add Link
          </Button>
        )}
      </Box>

      {isLoading && (
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}><Skeleton variant="rectangular" height={200} /></Grid>
          ))}
        </Grid>
      )}
      {isError && <Alert severity="error">Could not load the links for this class.</Alert>}
      {isEmpty && (
        <Typography variant="body2" color="text.secondary">
          {editable ? 'No links yet. Add a YouTube video or a website for this class.' : 'No links shared for this class yet.'}
        </Typography>
      )}
      {!!links?.length && (
        <Grid container spacing={2}>
          {links.map((link) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={link.id}>
              <LinkCard
                link={link}
                editable={editable}
                onEdit={(l) => { setEditing(l); setDialogOpen(true); }}
                onDelete={(l) => setToDelete(l)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {editable && (
        <ClassLinkDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          lessonId={lessonId}
          programLevel={programLevel}
          link={editing}
        />
      )}
      <ConfirmDialog
        open={!!toDelete}
        title="Remove link"
        message={toDelete ? `Remove "${toDelete.title}" from this class? Families will no longer see it.` : ''}
        confirmLabel="Remove"
        confirmColor="error"
        loading={deleteLink.isPending}
        onConfirm={async () => {
          if (!toDelete) return;
          await deleteLink.mutateAsync(toDelete.id);
          setToDelete(null);
        }}
        onCancel={() => setToDelete(null)}
      />
    </Box>
  );
}
